import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, Loader2 } from "lucide-react";
import { fetchTerm, sendChat } from "../lib/api";

/**
 * Singleton "one open at a time" gate — per 03_DESIGN_GUIDELINES §5.
 */
let openSetter = null;

function useSingletonOpen(setOpen) {
  useEffect(() => {
    if (!setOpen) return undefined;
    return () => {
      if (openSetter === setOpen) openSetter = null;
    };
  }, [setOpen]);
  return useCallback(() => {
    if (openSetter && openSetter !== setOpen) {
      openSetter(false);
    }
    openSetter = setOpen;
    setOpen(true);
  }, [setOpen]);
}

const PANEL_W = 360;
const MARGIN = 12;

/**
 * Compute a position for the panel that:
 *   - stays inside the viewport (both axes)
 *   - never covers the sentence it came from (prefers the side with more room)
 *   - falls back to below the trigger on narrow screens
 * Returns { top, left, width, placement }.
 */
function computePlacement(rect) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const spaceRight = vw - rect.right;
  const spaceLeft = rect.left;
  const isNarrow = vw < 640;

  if (isNarrow) {
    const width = Math.min(360, vw - 2 * MARGIN);
    let left = rect.left;
    if (left + width + MARGIN > vw) left = vw - width - MARGIN;
    if (left < MARGIN) left = MARGIN;
    return {
      top: rect.bottom + 10,
      left,
      width,
      placement: "bottom",
    };
  }

  if (spaceRight >= PANEL_W + MARGIN) {
    return {
      top: rect.top,
      left: rect.right + MARGIN,
      width: PANEL_W,
      placement: "right",
    };
  }
  if (spaceLeft >= PANEL_W + MARGIN) {
    return {
      top: rect.top,
      left: rect.left - PANEL_W - MARGIN,
      width: PANEL_W,
      placement: "left",
    };
  }
  const width = Math.min(PANEL_W, vw - 2 * MARGIN);
  let left = rect.left;
  if (left + width + MARGIN > vw) left = vw - width - MARGIN;
  if (left < MARGIN) left = MARGIN;
  const top = Math.min(rect.bottom + 10, vh - 200);
  return { top, left, width, placement: "bottom" };
}

export default function HoverChat({ term, children }) {
  const [open, setOpen] = useState(false);
  const [preload, setPreload] = useState(null);
  const [label, setLabel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: PANEL_W, placement: "right" });

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const scrollRef = useRef(null);

  const requestOpen = useSingletonOpen(setOpen);

  // Lazy-fetch the preload on first open.
  useEffect(() => {
    if (!open || preload !== null) return;
    let alive = true;
    (async () => {
      try {
        const data = await fetchTerm(term);
        if (!alive) return;
        setPreload(data.preload || "");
        setLabel(data.label || term);
      } catch {
        if (!alive) return;
        setPreload("Contextual chat is warming up — please try again shortly.");
        setLabel(term);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, term, preload]);

  // Recompute placement whenever the panel opens, on resize, or on scroll.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;
    const update = () => {
      if (!triggerRef.current) return;
      setPos(computePlacement(triggerRef.current.getBoundingClientRect()));
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  // Esc / click-away closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const submit = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || sending) return;
    const nextHistory = [...messages, { role: "user", content: msg }];
    setMessages(nextHistory);
    setInput("");
    setSending(true);
    try {
      const data = await sendChat({
        term,
        preload,
        message: msg,
        history: messages,
        sessionId,
      });
      setSessionId(data.session_id || sessionId);
      setMessages([...nextHistory, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages([
        ...nextHistory,
        {
          role: "assistant",
          content: "Contextual chat is warming up — please try again shortly.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const panel = useMemo(
    () => (
      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="gg-glass"
            role="dialog"
            aria-label={`Contextual chat: ${label || term}`}
            data-testid={`hoverchat-panel-${term}`}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width,
              zIndex: 200,
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              maxHeight: "min(70vh, 460px)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <div>
                <div className="gg-eyebrow" style={{ fontSize: 10 }}>
                  Contextual
                </div>
                <div
                  style={{
                    fontFamily: "Fraunces, Georgia, serif",
                    fontSize: 18,
                    lineHeight: 1.2,
                    color: "var(--paper)",
                    marginTop: 2,
                  }}
                >
                  {label || term}
                </div>
              </div>
              <button
                type="button"
                aria-label="Close contextual chat"
                onClick={() => setOpen(false)}
                data-testid={`hoverchat-close-${term}`}
                style={{
                  background: "transparent",
                  border: "1px solid var(--line)",
                  borderRadius: 8,
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--text-dim)",
                  cursor: "pointer",
                }}
              >
                <X size={14} />
              </button>
            </div>

            <div
              ref={scrollRef}
              style={{
                flex: 1,
                minHeight: 60,
                overflowY: "auto",
                fontSize: 13.5,
                lineHeight: 1.55,
                color: "var(--text-dim)",
                paddingRight: 4,
              }}
            >
              {preload && (
                <div
                  data-testid={`hoverchat-preload-${term}`}
                  style={{
                    margin: "6px 0 12px 0",
                    color: "var(--text-cloud)",
                    fontStyle: "italic",
                  }}
                >
                  {preload}
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  style={{
                    margin: "8px 0",
                    color:
                      m.role === "user"
                        ? "var(--gold-soft)"
                        : "var(--text-cloud)",
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      fontSize: 10,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: "var(--text-mute)",
                      marginBottom: 2,
                    }}
                  >
                    {m.role === "user" ? "You" : "Green Gold guide"}
                  </span>
                  {m.content}
                </div>
              ))}
              {sending && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: "var(--text-mute)",
                    marginTop: 8,
                  }}
                >
                  <Loader2 size={14} className="animate-spin" /> Thinking…
                </div>
              )}
            </div>

            <form
              onSubmit={submit}
              style={{
                marginTop: 10,
                display: "flex",
                gap: 6,
                borderTop: "1px solid var(--line)",
                paddingTop: 10,
              }}
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask a follow-up…"
                data-testid={`hoverchat-input-${term}`}
                style={{
                  flex: 1,
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid var(--line)",
                  borderRadius: 10,
                  padding: "8px 10px",
                  color: "var(--paper)",
                  fontSize: 13.5,
                  outline: "none",
                }}
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid={`hoverchat-send-${term}`}
                style={{
                  background:
                    "linear-gradient(135deg, var(--gold), var(--gold-deep))",
                  color: "var(--night)",
                  border: "none",
                  borderRadius: 10,
                  padding: "0 12px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontWeight: 600,
                  cursor: sending || !input.trim() ? "default" : "pointer",
                  opacity: sending || !input.trim() ? 0.6 : 1,
                }}
              >
                <Send size={14} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    ),
    [open, pos, preload, label, term, messages, sending, input]
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="gg-hover-trigger"
        onClick={(e) => {
          e.preventDefault();
          if (open) setOpen(false);
          else requestOpen();
        }}
        onMouseEnter={() => {
          if (!open) requestOpen();
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid={`hoverchat-trigger-${term}`}
      >
        {children}
        <sup aria-hidden>💬</sup>
      </button>
      {typeof document !== "undefined" && createPortal(panel, document.body)}
    </>
  );
}
